import { FC, useMemo } from 'react';
import { Line, Shape } from 'react-konva';
import type { Context as KonvaContext } from 'konva/lib/Context';
import type { Shape as KonvaShape } from 'konva/lib/Shape';
import { LayoutCanvas } from '../../../../../types/types';
import {
  LAYOUT_GRID_DOT_COLOR,
  LAYOUT_GRID_DOT_RADIUS,
  LAYOUT_GRID_LINE_COLOR,
} from '../../../../../constants';
import { getCellBoundaries } from './utils/getCellBoundaries';

interface LayoutGridProps {
  canvas: LayoutCanvas;
}

const LayoutGrid: FC<LayoutGridProps> = ({ canvas }) => {
  const {
    width,
    height,
    gridX = 0,
    gridY = 0,
    gapX = 0,
    gapY = 0,
    showPoints,
    showVLines,
    showHLines,
  } = canvas;

  const verticalPositions = useMemo(
    () => getCellBoundaries(width, gridX, gapX),
    [width, gridX, gapX]
  );
  const horizontalPositions = useMemo(
    () => getCellBoundaries(height, gridY, gapY),
    [height, gridY, gapY]
  );

  const drawDots = (context: KonvaContext, shape: KonvaShape): void => {
    context.beginPath();
    verticalPositions.forEach((x) => {
      horizontalPositions.forEach((y) => {
        context.moveTo(x + LAYOUT_GRID_DOT_RADIUS, y);
        context.arc(x, y, LAYOUT_GRID_DOT_RADIUS, 0, Math.PI * 2, false);
      });
    });
    context.fillStrokeShape(shape);
  };

  return (
    <>
      {showVLines &&
        verticalPositions.map((x) => (
          <Line
            key={`v-${x}`}
            points={[x, 0, x, height]}
            stroke={LAYOUT_GRID_LINE_COLOR}
            strokeWidth={1}
            listening={false}
          />
        ))}
      {showHLines &&
        horizontalPositions.map((y) => (
          <Line
            key={`h-${y}`}
            points={[0, y, width, y]}
            stroke={LAYOUT_GRID_LINE_COLOR}
            strokeWidth={1}
            listening={false}
          />
        ))}
      {showPoints && verticalPositions.length > 0 && (
        <Shape
          fill={LAYOUT_GRID_DOT_COLOR}
          listening={false}
          sceneFunc={drawDots}
        />
      )}
    </>
  );
};

export default LayoutGrid;
